import { Internationalization } from "@syncfusion/ej2-base";
import { Query } from "@syncfusion/ej2-data";
import {
  Aggregate,
  ColumnDirective,
  ColumnMenu,
  ColumnsDirective,
  CommandColumn,
  Filter,
  GridComponent,
  Inject,
  Page,
  Sort,
} from "@syncfusion/ej2-react-grids";
import { DialogComponent, DialogUtility } from "@syncfusion/ej2-react-popups";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  and,
  or,
} from "firebase/firestore";
import PropTypes from "prop-types";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Controller, useForm } from "react-hook-form";
import { FaEdit, FaSave, FaTimes } from "react-icons/fa";
import { v4 as uuidv4 } from "uuid";
import { config } from "../../src/utils/config";
import toggleDown from "../assets/disaster-icons/toggle-down-white.svg";
import AddNewButton from "../components/AddNewButton";
import CustomInput from "../components/CustomInput";
import CustomSelect from "../components/CustomSelect";
import DatePickerTemplate from "../components/DatePickerTemplate";
import DeleteModal from "../components/DeleteModal";
import Modal from "../components/Modal";
import PhotoColumn from "../components/PhotoColumn";
import SimpleFileUploader from "../components/SimpleFileUploader";
import CustomSwitch from "../components/Switch";
import ThreeDotMenu from "../components/ThreeDotMenu";
import ViewPhotosModal from "../components/ViewPhotosModal";
import countiesList from "../counties_list.json";
import { db } from "../firebase";
import mailer from "../service/api/mailer";
import citizenProfileService from "../service/citizen/CitizenProfile";
import {
  EthnicityOptions,
  GenderOptions,
  PetsCoatOptions,
  PetsSizeOptions,
  RelationshipOptions,
} from "../utils/constants";
import { isValidDate } from "../utils/formatter";
import helpers from "../utils/helpers";
import { alphabetRegex } from "../utils/regex";
import { USStates } from "./datasource";
import RecoveryTrackerOptions from "../components/RecoveryTrackerOptions";
import { useNavigate } from "react-router-dom";
import { useRecoveryTracker } from "../context/RecoveryTrackerContext";
const sortSettings = {
  allowSorting: true,
};

const columnMenuItems = ["SortAscending", "SortDescending", "Filter"];

const pageSettings = { pageSize: 10 };

const editing = {
  allowDeleting: true,
  allowAdding: true,
  allowEditing: true,
  mode: "Dialog",
  showDeleteConfirmDialog: true,
  dialog: { cssClass: "custom-dialog" },
};

const filterSettings = {
  type: "CheckBox",
};

const intl = new Internationalization();

const formatDate = (timestamp) => {
  if (timestamp) {
    const date = new Date(timestamp); // Assuming timestamp is already a string
    return date.toLocaleDateString("en-US"); // Format as MM/DD/YYYY
  }
  return "";
};

const militaryVetTypes = [{ value: "Yes" }, { value: "No" }];
const relationshipTypes = [
  { value: "Husband" },
  { value: "Wife" },
  { value: "Daughter" },
  { value: "Son" },
  { value: "Other Relatives" },
];

const genderTypes = [{ value: "Male" }, { value: "Female" }];

const ethnicityTypes = [
  { value: "American Indian or Alaska Native" },
  { value: "Asian" },
  { value: "Hispanic/ Latino" },
  { value: "Native Hawaiian or Pacific Islander" },
  { value: "Tribal Affiliation" },
  { value: "Other" },
  { value: "White" },
  { value: "None of the Above" },
];

const petSizeTypes = [
  { value: "Small" },
  { value: "Medium" },
  { value: "Large" },
];

const petCoatTypes = [
  { value: "Wire" },
  { value: "Straight" },
  { value: "Shaggy" },
  { value: "Curly" },
  { value: "Wavy" },
];

const militaryvetParams = {
  params: {
    allowFiltering: true,
    dataSource: militaryVetTypes,
    fields: { text: "value", value: "value" },
    query: new Query(),
  },
};

const relationshipParams = {
  params: {
    allowFiltering: false,
    dataSource: relationshipTypes,
    fields: { text: "value", value: "value" },
    query: new Query(),
  },
};

const genderParams = {
  params: {
    allowFiltering: false,
    dataSource: genderTypes,
    fields: { text: "value", value: "value" },
    query: new Query(),
  },
};

const ethnicityParams = {
  params: {
    allowFiltering: false,
    dataSource: ethnicityTypes,
    fields: { text: "value", value: "value" },
    query: new Query(),
  },
};

const petSizeParams = {
  params: {
    allowFiltering: false,
    dataSource: petSizeTypes,
    fields: { text: "value", value: "value" },
    query: new Query(),
  },
};

const petCoatParams = {
  params: {
    allowFiltering: true,
    dataSource: petCoatTypes,
    fields: { text: "value", value: "value" },
    query: new Query(),
  },
};

// Add this near the top with other constant definitions
const additionalInsuranceTypes = [
  { value: "Flood insurance" },
  { value: "Earthquake additional coverage" },
  { value: "Other disaster-related additional coverage" },
  { value: "I did not have additional insurance" },
];

const additionalInsuranceTypeParams = {
  params: {
    allowFiltering: true,
    dataSource: [
      { text: "Flood insurance", value: "Flood insurance" },
      {
        text: "Earthquake additional coverage",
        value: "Earthquake additional coverage",
      },
      {
        text: "Other disaster-related additional coverage",
        value: "Other disaster-related additional coverage",
      },
      {
        text: "I did not have additional insurance",
        value: "I did not have additional insurance",
      },
    ],
    fields: { text: "text", value: "value" },
    query: new Query(),
  },
};

const CitizenProfile = () => {
  // const [userId, setUserId] = useState(null);
  const [data, setData] = useState(null);
  const [isPersonalInfoVisible, setIsPersonalInfoVisible] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [tempFormData, setTempFormData] = useState({});
  const [lastEditedTime, setLastEditedTime] = useState(null);
  const [householdMembers, setHouseholdMembers] = useState();
  const [validationErrors, setValidationErrors] = useState({});
  const errorTimeoutRef = useRef(null);
  // const [dob, setDob] = useState(null);
  // const [householdId, setHouseholdId] = useState(null);
  const selectedUserId = sessionStorage.getItem("userId");

  // HOUSEHOLD MEMBERS
  const [activeMenuHouseholdMember, setActiveMenuHouseholdMember] =
    useState(null);
  const [isEditingHouseholdMember, setIsEditingHouseholdMember] =
    useState(false);
  const [showHouseholdMemberModal, setShowHouseholdMemberModal] =
    useState(false);
  const householdMemberFormRef = useRef(null);
  const [isSubmittingHouseholdMember, setIsSubmittingHouseholdMember] =
    useState(false);
  const [householdMemberToDeleteId, setHouseholdMemberToDeleteId] =
    useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedInviteHouseholdMember, setSelectedInviteHouseholdMember] =
    useState(null);

  // PETS
  const [activeMenuPets, setActiveMenuPets] = useState(null);
  const [isEditingPets, setIsEditingPets] = useState(false);
  const [showPetsModal, setShowPetsModal] = useState(false);
  const petsFormRef = useRef(null);
  const [isSubmittingPets, setIsSubmittingPets] = useState(false);
  const [petsToDeleteId, setPetsToDeleteId] = useState(null);
  const [showDeletePetModal, setShowDeletePetModal] = useState(false);

  // VIEW PET PHOTOS MODAL
  const [showPetPhotosModal, setShowPetPhotosModal] = useState(false);
  const [selectedPet, setSelectedPet] = useState({});
  const [selectedPetId, setSelectedPetId] = useState(null);
  const [accessOptions, setAccessOptions] = useState([]);
  const navigate = useNavigate();
  const { setShowRecoveryTracker } = useRecoveryTracker();
  // useEffect(() => {
  //   const auth = getAuth();

  //   const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
  //     if (currentUser) {
  //       // setUserId(selectedUserId);
  //     } else {
  //       setData(null);
  //     }
  //   });

  //   return () => unsubscribe();
  // }, []);
  const fetchHouseholdMembers = useCallback(async () => {
    let householdMembersData = [];
    if (!data?.householdId || !data?.householdMemberId) {
      const citizenData = await citizenProfileService.getCitizenProfile(
        "users",
        selectedUserId
      );
      if (citizenData) {
        householdMembersData = (
          await citizenProfileService.getHouseholdMembers(
            citizenData.household_id,
            citizenData.household_member_id
          )
        ).filter(
          (member) =>
            !member.is_deleted &&
            member.household_member_id !== citizenData.household_member_id
        );
      }
    } else {
      householdMembersData = (
        await citizenProfileService.getHouseholdMembers(
          data?.householdId,
          data?.householdMemberId
        )
      ).filter(
        (member) =>
          !member.is_deleted &&
          member.household_member_id !== data?.householdMemberId
      );
    }
    setHouseholdMembers(
      householdMembersData.map((member) => ({
        ...member,
        date_of_birth: member.date_of_birth || "mm/dd/yyyy",
      }))
    );
  }, [data, selectedUserId]);
  const fetchPets = useCallback(async () => {
    let petsData = [];
    if (!data?.household_id) {
      const citizenData = await citizenProfileService.getCitizenProfile(
        "users",
        selectedUserId
      );
      if (citizenData) {
        petsData = (
          await citizenProfileService.getPets(citizenData.household_id)
        ).filter((pet) => !pet.is_deleted);
      }
    } else {
      petsData = (
        await citizenProfileService.getPets(data.household_id)
      ).filter((pet) => !pet.is_deleted);
    }
    setPets(petsData);
  }, [selectedUserId, data]);

  const fetchData = useCallback(async () => {
    const citizenData = await citizenProfileService.getCitizenProfile(
      "users",
      selectedUserId
    );
    if (citizenData) {
      setData(citizenData);
      setTempFormData(citizenData);
      if (citizenData.updatedData_at)
        setLastEditedTime(
          citizenData.updated_at instanceof Date
            ? citizenData.updated_at
            : new Date(citizenData.updated_at)
        );
      // setHouseholdId(citizenData.household_id);
      const householdData = await citizenProfileService.getHouseholdInfo(
        citizenData.household_id
      );
      setHouseholdFormData({
        ...householdData,
        disability: citizenData.disability,
        military_vet: citizenData.military_vet,
      });
      setTempHouseholdFormData({
        ...householdData,
        disability: citizenData.disability,
        military_vet: citizenData.military_vet,
      });

      await fetchHouseholdMembers();

      // Fetch pets - simplified filter
      await fetchPets();
    }
  }, [fetchHouseholdMembers, fetchPets, selectedUserId]);
  useEffect(() => {
    if (selectedUserId) {
      fetchData();
    }
  }, [selectedUserId]);
  const togglePersonalInfo = (event) => {
    event.preventDefault();
    setIsPersonalInfoVisible(!isPersonalInfoVisible);
  };

  const startEditing = (event) => {
    event.preventDefault();
    setTempFormData({ ...data });
    setIsEditMode(true);
  };

  const saveChanges = async (event) => {
    event.preventDefault();
    const errors = validatePersonalInfo(tempFormData);

    // Add validation for military and disability
    if (tempHouseholdFormData.military_vet === undefined) {
      errors.military_vet = "Please select if you are Active Military / Vet";
    }
    if (tempHouseholdFormData.disability === undefined) {
      errors.disability = "Please select if you have a disability";
    }

    // Check if email has changed
    if (tempFormData.email && tempFormData.email !== data.email) {
      // Query Firestore for existing users with this email
      const usersRef = collection(db, "users");
      const q = query(
        usersRef,
        where("email", "==", tempFormData.email.toLowerCase())
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        errors.email = "Email already taken";
        setValidationErrors(errors);
        return; // Add this return to prevent proceeding with save
      }
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);

      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }

      errorTimeoutRef.current = setTimeout(() => {
        setValidationErrors({});
      }, 5000);

      return;
    }

    // Check if all required fields are filled
    const requiredFields = [
      "first_name",
      "last_name",
      "birth_date",
      "address1",
      "city",
      "state",
      "zip",
      "county",
      "mobile_number",
      "alternate_phone",
      // "email",
      "alternate_contact_name",
      "alternate_email",
      "alternate_mobile_number",
      "ethnicity",
      "gender",
    ];

    const isProfileComplete = requiredFields.every((field) => {
      const value = tempFormData[field];
      return value !== undefined && value !== null && value !== "";
    });

    // Additional checks for disability and military_vet
    const isHouseholdComplete =
      tempHouseholdFormData.disability !== undefined &&
      tempHouseholdFormData.military_vet !== undefined;

    if (isProfileComplete && isHouseholdComplete) {
      // Check citizen's status only if profile is complete
      const userDoc = await getDoc(doc(db, "users", selectedUserId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData.status !== "ACTIVE") {
          setShowCompletionModal(true);
        }
      }
    }

    await savePersonalInfo();
  };

  const fetchAccessOptions = async () => {
    try {
      const userDocRef = doc(db, "users", selectedUserId);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        let centerId = userDoc.data().center_id;
        if (centerId === "") {
          centerId = "1234";
        }

        // Query for partners
        const partnersQuery = query(
          collection(db, "users"),
          and(
            or(
              where("center_id", "array-contains", centerId),
              where("center_id", "==", centerId)
            ),
            where("user_role", "==", "partner")
          )
        );

        // Query for centers - update to only get the user's center
        const centersQuery = query(
          collection(db, "users"),
          where("user_role", "==", "center"),
          where("center_id", "==", centerId)
        );

        // Execute both queries
        const [partnersSnapshot, centersSnapshot] = await Promise.all([
          getDocs(partnersQuery),
          getDocs(centersQuery),
        ]);

        console.log("Partners found:", partnersSnapshot.size);
        console.log("Centers found:", centersSnapshot.size);

        // Process partner options
        const partnerOptions = await Promise.all(
          partnersSnapshot.docs.map(async (document) => {
            const userData = document.data();
            const partnerDoc = await getDoc(
              doc(db, "partners", userData.partner_id)
            );

            if (partnerDoc.exists()) {
              const partnerData = partnerDoc.data();
              return {
                id: document.id,
                name: partnerData.name_of_org,
                type: "partner",
              };
            }
            return null;
          })
        );

        // Process center options
        const centerOptions = await Promise.all(
          centersSnapshot.docs.map(async (document) => {
            const userData = document.data();
            const centerDoc = await getDoc(
              doc(db, "centers", userData.center_id)
            );

            if (centerDoc.exists()) {
              const centerData = centerDoc.data();
              return {
                id: document.id,
                name: centerData.name_of_hub,
                type: "center",
              };
            }
            return null;
          })
        );

        // Combine and filter out null values
        const allOptions = [...partnerOptions, ...centerOptions].filter(
          Boolean
        );
        console.log("Final options:", allOptions);
        setAccessOptions(allOptions);
      }
    } catch (error) {
      console.error("Error fetching access options:", error);
    }
  };

  const savePersonalInfo = async () => {
    const errors = validatePersonalInfo(tempFormData);
    console.log("errors", errors);

    // Add validation for military and disability
    if (tempHouseholdFormData.military_vet === undefined) {
      errors.military_vet = "Please select if you are Active Military / Vet";
    }
    if (tempHouseholdFormData.disability === undefined) {
      errors.disability = "Please select if you have a disability";
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);

      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }

      errorTimeoutRef.current = setTimeout(() => {
        setValidationErrors({});
      }, 5000);

      return;
    }

    try {
      const currentTime = new Date();
      // Format birth_date if it exists
      const formattedBirthDate = tempFormData.birth_date
        ? new Date(tempFormData.birth_date).toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
          })
        : "";

      const updatedData = {
        ...tempFormData,
        email: helpers.lowerCaseString(tempFormData.email),
        birth_date: formattedBirthDate,
        disability: tempHouseholdFormData.disability,
        military_vet: tempHouseholdFormData.military_vet,
        updated_at: currentTime,
      };

      // Check if address has changed - using safe null checks
      const addressChanged =
        data?.address1 !== updatedData.address1 ||
        data?.city !== updatedData.city ||
        data?.state !== updatedData.state ||
        data?.zip !== updatedData.zip ||
        data?.county !== updatedData.county;

      if (addressChanged) {
        // Only proceed if we have all required address fields
        if (
          updatedData.address1 &&
          updatedData.city &&
          updatedData.state &&
          updatedData.zip
        ) {
          try {
            // Get new coordinates from Google Maps API
            const address = `${updatedData.address1}, ${updatedData.city}, ${updatedData.state}, ${updatedData.zip}`;
            const response = await fetch(
              `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
                address
              )}&key=${config.google_maps}`
            );

            if (!response.ok) {
              throw new Error("Failed to fetch coordinates");
            }

            const geoData = await response.json();

            if (geoData.results && geoData.results.length > 0) {
              const { lat, lng } = geoData.results[0].geometry.location;
              updatedData.latitude = lat;
              updatedData.longitude = lng;
            }
          } catch (error) {
            console.error("Error getting coordinates:", error);
            // Continue with save even if coordinates update fails
          }
        }

        // console.log("updatedData is here.address_id: ", updatedData);

        //Update the primary address in address collection
        const addressQuery = query(
          collection(db, "address", updatedData.address_id, "addresses"),
          where("is_primary_address", "==", true)
        );
        const addressSnapshot = await getDocs(addressQuery);
        if (!addressSnapshot.empty) {
          const addressRef = addressSnapshot.docs[0].ref;
          await updateDoc(addressRef, {
            address1: updatedData.address1,
            address2: updatedData.address2,
            city: updatedData.city,
            state: updatedData.state,
            zip: updatedData.zip,
            county: updatedData.county,
            updated_at: currentTime,
          });
        }

        // Check if county has changed and update center_id if necessary
        if (data?.county !== updatedData.county && updatedData.county) {
          try {
            // Query for centers in the new county
            const centersQuery = query(
              collection(db, "users"),
              where("user_role", "==", "center"),
              where("county", "==", updatedData.county)
            );

            const centersSnapshot = await getDocs(centersQuery);

            if (!centersSnapshot.empty) {
              // Get the first matching center's center_id
              const centerDoc = centersSnapshot.docs[0];
              const centerData = centerDoc.data();
              if (centerData?.center_id) {
                updatedData.center_id = centerData.center_id;
              }
            } else {
              alert("No centers found in the new county");
              updatedData.center_id = "";
            }
          } catch (error) {
            console.error("Error updating center assignment:", error);
            // Continue with save even if center reassignment fails
          }
        }
      }

      const requiredFields = [
        "first_name",
        "last_name",
        "mobile_number",
        "alternate_phone",
        "address1",
        "city",
        "state",
        "zip",
        "county",
        "alternate_contact_name",
        "alternate_email",
        "alternate_mobile_number",
        "birth_date",
        // "citizenship",
        "ethnicity",
        "gender",
        "military_vet",
        "disability",
      ];

      const isProfileComplete = requiredFields.every((field) => {
        const value = updatedData[field];
        return value !== undefined && value !== null && value !== "";
      });

      if (isProfileComplete) {
        updatedData.status = "ACTIVE";
      }

      // Save the updated profile with the potentially updated status
      await citizenProfileService.updateCitizenProfile(
        "users",
        selectedUserId,
        updatedData
      );

      // Update local state
      setData(updatedData);
      setIsEditMode(false);
      setLastEditedTime(currentTime);
      setValidationErrors({});
    } catch (error) {
      console.error("Error updating profile:", error);
      // Optionally show error to user
      setValidationErrors({
        general: "Failed to update profile. Please try again.",
      });
    }
  };

  const cancelEditing = (event) => {
    event.preventDefault();
    setTempFormData({ ...data });
    setIsEditMode(false);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    // Fields that should only contain letters, spaces, and hyphens
    const nameFields = ["first_name", "last_name", "alternate_contact_name"];
    // Fields that should only contain letters, numbers, spaces, and basic punctuation
    const addressFields = ["address1", "address2", "city", "county"];

    let sanitizedValue = value;
    let errors = { ...validationErrors };
    let shouldUpdateForm = true; // Flag to determine if we should update form

    if (nameFields.includes(name)) {
      // Only allow letters, spaces, and hyphens
      sanitizedValue = value.replace(/[^a-zA-Z\s-]/g, "");
    } else if (addressFields.includes(name)) {
      // Only allow letters, numbers, spaces, periods, commas, and hyphens
      sanitizedValue = value.replace(/[^a-zA-Z0-9\s.,#-]/g, "");
    } else if (
      name === "mobile_number" ||
      name === "alternate_phone" ||
      name === "alternate_mobile_number"
    ) {
      // Only allow numbers and hyphens for phone numbers
      sanitizedValue = value.replace(/[^0-9-]/g, "");
    } else if (name === "zip") {
      // Only allow numbers for zip code
      sanitizedValue = value.replace(/[^0-9]/g, "");
    } else if (name === "alternate_email" || name === "email") {
      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      // Allow empty value or partial valid email
      if (value === "" || emailRegex.test(value)) {
        delete errors[name];
      } else {
        errors[name] = "Invalid Email";
        // Only update form if the email is empty or matches basic email pattern
        const basicEmailPattern = /^[^\s@]*@?[^\s@]*\.?[^\s@]*$/;
        shouldUpdateForm = basicEmailPattern.test(value);
      }
    }

    setValidationErrors(errors);

    if (shouldUpdateForm) {
      setTempFormData((prevData) => ({
        ...prevData,
        [name]: sanitizedValue,
      }));
    }
  };

  const handleInputBlur = async () => {
    const errors = validatePersonalInfo(tempFormData);

    // Check if email has changed
    if (
      tempFormData.email &&
      tempFormData.email.trim() !== "" &&
      tempFormData.email !== data.email
    ) {
      // Query Firestore for existing users with this email
      const usersRef = collection(db, "users");
      const q = query(
        usersRef,
        where("email", "==", tempFormData.email.toLowerCase())
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        errors.email = "Email already taken";
        setValidationErrors(errors);
        return; // Prevent saving if email is taken
      }
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);

      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }

      errorTimeoutRef.current = setTimeout(() => {
        setValidationErrors({});
      }, 5000);

      return;
    }

    const currentTime = new Date();
    const updatedData = {
      ...tempFormData,
      disability: tempHouseholdFormData.disability,
      military_vet: tempHouseholdFormData.military_vet,
      home_insurance: tempHouseholdFormData.home_insurance,
      automotive_insurance: tempHouseholdFormData.automotive_insurance,
      updated_at: currentTime,
    };

    try {
      await citizenProfileService.updateCitizenProfile(
        "users",
        selectedUserId,
        updatedData
      );
      setData(updatedData);
      setLastEditedTime(currentTime);
      setValidationErrors({});
    } catch (error) {
      console.error("Error updating profile: ", error);
    }
  };

  const handleHouseholdInputBlur = async () => {
    const errors = validateHouseholdInfo(tempHouseholdFormData);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
      errorTimeoutRef.current = setTimeout(() => {
        setValidationErrors({});
      }, 5000);
      return;
    }

    try {
      const currentTime = new Date();

      // Remove nested household object and other unnecessary fields
      const { household, id, ...rawData } = tempHouseholdFormData;

      // Create a clean object with only the fields we want
      const updatedHouseholdData = {
        annual_income: rawData.annual_income || "",
        owner_or_renter: rawData.owner_or_renter || "",
        insurance: Boolean(rawData.insurance),
        household_size: rawData.household_size || "",
        home_insurance: Boolean(rawData.home_insurance),
        home_insurance_type: rawData.home_insurance_type || "",
        automotive_insurance: Boolean(rawData.automotive_insurance),
        automotive_insurance_type: rawData.automotive_insurance_type || "",
        additional_insurance_type: rawData.home_insurance_type || "",
        updated_at: currentTime,
      };

      if (!data.household_id) {
        console.error("No household ID found");
        return;
      }

      // Update the backend
      await citizenProfileService.updateHouseholdInfo(
        data.household_id,
        updatedHouseholdData
      );

      // Update the frontend state with the clean data
      setHouseholdFormData(updatedHouseholdData);
      setTempHouseholdFormData(updatedHouseholdData);
      // setLastHouseholdEditedTime(currentTime);
      setIsHouseholdEditMode(false);
      setValidationErrors({});
    } catch (error) {
      console.error("Error updating household profile: ", error);
    }
  };

  const renderField = (
    label,
    name,
    placeholder,
    isDropdown = false,
    className = "",
    style = {} // Add style parameter
  ) => {
    const requiredFields = [
      "first_name",
      "last_name",
      "birth_date",
      "address1",
      "city",
      "state",
      "zip",
      "mobile_number",
      "alternate_phone",
      "email",
      "alternate_contact_name",
      "alternate_email",
      "alternate_mobile_number",
      "county",
      "gender",
      // "citizenship",
      "ethnicity",
    ];
    const isRequired = requiredFields.includes(name);

    // Get counties for the selected state
    const getCountiesForState = (state) => {
      if (!state) return [];
      // Handle both full name and abbreviation
      const stateObj = USStates.find((s) => s.abbreviation === state);
      const stateName = stateObj ? stateObj.name : state;
      return countiesList
        .filter((county) => county.State === stateName)
        .map((county) => county.County);
    };

    return (
      <div
        className={`flex flex-col items-start justify-start gap-1 ${className}`}
      >
        <div className="text-gray-600 relative flex items-center text-left text-xs leading-[20px]">
          {label}
          {isRequired && (
            <p className="absolute right-[-15px] top-[4px] text-[26px] text-red-500">
              *
            </p>
          )}
        </div>
        <div
          className={`self-stretch rounded-md ${
            isEditMode ? "bg-white" : "bg-gray-200"
          } box-border flex h-10 flex-row items-center justify-between border-[1px] border-solid ${
            validationErrors[name] ? "border-red-500" : "border-gray-300"
          } px-3 py-2`}
          style={style}
        >
          {isEditMode ? (
            name === "birth_date" ? (
              <DatePickerTemplate
                value={tempFormData[name || ""]}
                onChange={(val) =>
                  handleInputChange({ target: { name, value: val } })
                }
                style={style}
              />
            ) : isDropdown ? (
              <select
                className="w-full bg-white text-left text-xs text-black [border:none] [outline:none]"
                name={name}
                value={tempFormData[name] || ""}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
              >
                <option value="" disabled>
                  {placeholder}
                </option>
                {name === "ethnicity" ? (
                  <>
                    <option value="African American or Black">
                      African American or Black
                    </option>
                    <option value="American Indian or Alaska Native">
                      American Indian or Alaska Native
                    </option>
                    <option value="Asian">Asian</option>
                    <option value="Hispanic/ Latino">Hispanic/ Latino</option>
                    <option value="Native Hawaiian or Pacific Islander">
                      Native Hawaiian or Pacific Islander
                    </option>
                    <option value="Tribal Affiliation">
                      Tribal Affiliation
                    </option>
                    <option value="Other">Other</option>
                    <option value="White">White</option>
                    <option value="None of the Above">None of the Above</option>
                  </>
                ) : name === "gender" ? (
                  <>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </>
                ) : name === "county" ? (
                  getCountiesForState(tempFormData.state).map((county) => (
                    <option key={county} value={county}>
                      {county}
                    </option>
                  ))
                ) : name === "state" ? (
                  USStates.map((state) => (
                    <option key={state.stateID} value={state.abbreviation}>
                      {state.abbreviation}
                    </option>
                  ))
                ) : null}
              </select>
            ) : (
              <input
                className="w-full bg-white text-left text-xs text-black [border:none] [outline:none]"
                type="text"
                name={name}
                value={tempFormData[name] || ""}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                placeholder={placeholder}
                style={style}
              />
            )
          ) : (
            <div
              className={`flex-1 text-xs ${
                tempFormData[name]
                  ? "font-semibold text-gray-800"
                  : "text-gray-500"
              } text-left`}
            >
              {tempFormData[name] || placeholder}
            </div>
          )}
        </div>
        {isEditMode && validationErrors[name] && (
          <div className="mt-1 text-xs text-red-500">
            {validationErrors[name]}
          </div>
        )}
      </div>
    );
  };

  const validatePersonalInfo = (formData) => {
    const errors = {};
    const requiredFields = [
      "first_name",
      "last_name",
      "birth_date",
      "address1",
      "city",
      "state",
      "zip",
      "mobile_number",
      "alternate_phone",
      // "email",
      "alternate_contact_name",
      "alternate_email",
      "alternate_mobile_number",
      // "citizenship",
      "ethnicity",
      "gender",
    ];

    requiredFields.forEach((field) => {
      if (
        !formData[field] ||
        (typeof formData[field] === "string" && formData[field].trim() === "")
      ) {
        errors[field] = "This field is required";
      }
    });

    // Add email validation for alternate_email
    if (formData.alternate_email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.alternate_email)) {
        errors.alternate_email = "Invalid Email";
      }
    }
    if (formData.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        errors.email = "Invalid Email";
      }
    }

    return errors;
  };

  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    const isNewlyRegistered = localStorage.getItem("newlyRegistered");
    if (isNewlyRegistered === "true") {
      setShowPopup(true);
      localStorage.removeItem("newlyRegistered"); // Remove the flag after showing the popup
    }
  }, []);

  const handleClosePopup = () => {
    setShowPopup(false);
  };

  const dialogContent = () => {
    return (
      <div style={{ textAlign: "center", padding: "20px" }}>
        <h2
          style={{
            fontSize: "24px",
            fontWeight: "normal",
            color: "#1e1919",
            marginBottom: "10px",
          }}
        >
          Welcome to your Citizen Profile!
        </h2>
        <p style={{ fontSize: "16px", color: "#637381", marginBottom: "20px" }}>
          Please finish your profile to get the most out of our services.
        </p>
        <button
          onClick={handleClosePopup}
          className="btn-default"
          style={{
            padding: "10px 20px",
            cursor: "pointer",
          }}
        >
          Got it!
        </button>
      </div>
    );
  };

  const [isHouseholdInfoVisible, setIsHouseholdInfoVisible] = useState(true);
  const [householdFormData, setHouseholdFormData] = useState({
    annual_income: "",
    ssn: "",
    owner_or_renter: "",
    insurance: false,
    household_size: "",
    disability: false,
    military_vet: false,
    home_insurance: false,
    home_insurance_type: "",
    automotive_insurance: false,
    automotive_insurance_type: "",
    additional_insurance: false,
    additional_insurance_type: "",
  });
  const [tempHouseholdFormData, setTempHouseholdFormData] = useState({
    ...householdFormData,
  });
  // const [lastHouseholdEditedTime, setLastHouseholdEditedTime] = useState(null);

  const [isHouseholdEditMode, setIsHouseholdEditMode] = useState(false);

  const toggleHouseholdInfo = (event) => {
    event.preventDefault();
    setIsHouseholdInfoVisible(!isHouseholdInfoVisible);
  };

  const startHouseholdEditing = (event) => {
    event.preventDefault();
    setIsHouseholdEditMode(true);
  };

  const validateHouseholdInfo = (formData) => {
    const errors = {};
    const requiredFields = ["owner_or_renter", "insurance", "household_size"];

    requiredFields.forEach((field) => {
      if (
        formData[field] === undefined ||
        formData[field] === "" ||
        formData[field] === null
      ) {
        errors[field] = "This field is required";
      }
    });

    return errors;
  };

  const saveHouseholdChanges = async (event) => {
    event.preventDefault();

    try {
      const currentTime = new Date();

      const updatedHouseholdData = {
        annual_income: String(tempHouseholdFormData.annual_income || ""),
        owner_or_renter: String(tempHouseholdFormData.owner_or_renter || ""),
        insurance: Boolean(tempHouseholdFormData.insurance),
        household_size: String(tempHouseholdFormData.household_size || ""),
        home_insurance: Boolean(tempHouseholdFormData.home_insurance),
        home_insurance_type: String(
          tempHouseholdFormData.home_insurance_type || ""
        ),
        automotive_insurance: Boolean(
          tempHouseholdFormData.automotive_insurance
        ),
        automotive_insurance_type: String(
          tempHouseholdFormData.automotive_insurance_type || ""
        ),
        additional_insurance: Boolean(
          tempHouseholdFormData.additional_insurance
        ),
        additional_insurance_type: String(
          tempHouseholdFormData.additional_insurance_type || ""
        ),
        updated_at: currentTime,
      };

      if (!data.household_id) {
        throw new Error("No household ID found");
      }

      const result = await citizenProfileService.updateHouseholdInfo(
        data.household_id,
        updatedHouseholdData
      );

      setHouseholdFormData(result);
      setTempHouseholdFormData(result);
      // setLastHouseholdEditedTime(currentTime);
      setIsHouseholdEditMode(false);
      setValidationErrors({});
    } catch (error) {
      console.error("Error updating household profile:", error);
    }
  };

  const cancelHouseholdEditing = (event) => {
    event.preventDefault();
    setTempHouseholdFormData({ ...householdFormData });
    setIsHouseholdEditMode(false);
  };

  const handleHouseholdInputChange = (event) => {
    const { name, value } = event.target;
    setTempHouseholdFormData((prevData) => ({
      ...prevData,
      [name]:
        name === "disability" ||
        name === "military_vet" ||
        name === "insurance" ||
        name === "home_insurance" ||
        name === "automotive_insurance"
          ? value === "Yes"
          : value,
    }));
  };

  const renderHouseholdField = (
    label,
    name,
    placeholder,
    isDropdown = false,
    className = "",
    isOptional = false
  ) => {
    const isRequired = !isOptional;

    const handleAnnualIncomeChange = (e) => {
      let inputValue = e.target.value;

      const numericValue = inputValue.replace(/[^0-9]/g, "");

      handleHouseholdInputChange({
        target: { name: name, value: numericValue ? `$${numericValue}` : "" },
      });
    };

    const validateAnnualIncome = () => {
      if (
        name === "annual_income" &&
        (!tempHouseholdFormData[name] || tempHouseholdFormData[name] === "$")
      ) {
        // Trigger validation error if the field is invalid
        setValidationErrors((prevErrors) => ({
          ...prevErrors,
          [name]: "Annual income is required",
        }));
      } else {
        // Remove the error if it's valid
        setValidationErrors((prevErrors) => {
          const { [name]: _, ...newErrors } = prevErrors;
          return newErrors;
        });
      }
    };

    const handleHouseholdSizeChange = (e) => {
      let inputValue = e.target.value;

      // Remove non-numeric characters
      const numericValue = inputValue.replace(/[^0-9]/g, "");

      // Convert to number and limit to 20
      let validNumber = numericValue
        ? Math.min(parseInt(numericValue, 10), 20)
        : "";

      handleHouseholdInputChange({
        target: { name: "household_size", value: validNumber },
      });
    };

    const validateHouseholdSize = () => {
      if (!tempHouseholdFormData["household_size"]) {
        setValidationErrors((prevErrors) => ({
          ...prevErrors,
          household_size: "Household size is required",
        }));
      } else {
        setValidationErrors((prevErrors) => {
          const { household_size, ...newErrors } = prevErrors;
          return newErrors;
        });
      }
    };

    return (
      <div
        className={`flex flex-col items-start justify-start gap-1 hover:cursor-pointer ${className}`}
      >
        <div className="text-gray-600 relative text-left  text-xs leading-[20px] hover:cursor-pointer">
          {label}
          {isRequired ? (
            <p className="absolute right-[-15px] top-[4px] text-[26px] text-red-500">
              *
            </p>
          ) : (
            <span className="ml-1 text-xs text-gray-400">(Optional)</span>
          )}
        </div>
        <div
          className={`self-stretch rounded-md ${
            isHouseholdEditMode ? "bg-white" : "bg-gray-200"
          } box-border flex h-10 flex-row items-center justify-between border-[1px] border-solid ${
            validationErrors[name] ? "border-red-500" : "border-gray-300"
          } px-3 py-2`}
        >
          {isHouseholdEditMode ? (
            name === "household_size" ? (
              // Custom Input for Household Size (Integer Only, Max 20)
              <input
                type="text"
                className="w-full bg-white text-left text-xs text-black [border:none] [outline:none]"
                placeholder={placeholder}
                name={name}
                value={tempHouseholdFormData[name] || ""}
                onChange={handleHouseholdSizeChange}
                onBlur={validateHouseholdSize}
              />
            ) : isDropdown ? (
              <select
                className="w-full bg-white text-left  text-xs text-black [border:none] [outline:none] hover:cursor-pointer"
                name={name}
                value={
                  name === "insurance"
                    ? tempHouseholdFormData[name]
                      ? "Yes"
                      : "No"
                    : tempHouseholdFormData[name] || ""
                }
                onChange={handleHouseholdInputChange}
                // onBlur={handleHouseholdInputBlur}
              >
                <option value="" disabled>
                  {placeholder}
                </option>
                {name === "owner_or_renter" ? (
                  <>
                    <option value="Owner" className="hover:cursor-pointer">
                      Owner
                    </option>
                    <option value="Renter" className="hover:cursor-pointer">
                      Renter
                    </option>
                    <option
                      value="Rent to Own"
                      className="hover:cursor-pointer"
                    >
                      Rent to Own
                    </option>
                  </>
                ) : name === "annual_income" ? (
                  <>
                    <option value="0-20000" className="hover:cursor-pointer">
                      0-$20,000
                    </option>
                    <option
                      value="20001-40000"
                      className="hover:cursor-pointer"
                    >
                      $20,001-$40,000
                    </option>
                    <option
                      value="40001-60000"
                      className="hover:cursor-pointer"
                    >
                      $40,001-$60,000
                    </option>
                    <option
                      value="60001-80000"
                      className="hover:cursor-pointer"
                    >
                      $60,001-$80,000
                    </option>
                    <option
                      value="80001-100000"
                      className="hover:cursor-pointer"
                    >
                      $80,001-$100,000
                    </option>
                    <option value="100000+" className="hover:cursor-pointer">
                      Greater than $100,000
                    </option>
                  </>
                ) : name === "home_insurance_type" ? (
                  <>
                    <option value="Homeowners" className="hover:cursor-pointer">
                      Homeowners
                    </option>
                    <option value="Renter" className="hover:cursor-pointer">
                      Renter
                    </option>
                  </>
                ) : name === "additional_insurance_type" ? (
                  <>
                    <option
                      value="Flood insurance"
                      className="hover:cursor-pointer"
                    >
                      Flood insurance
                    </option>
                    <option
                      value="Earthquake additional coverage"
                      className="hover:cursor-pointer"
                    >
                      Earthquake additional coverage
                    </option>
                    <option
                      value="Other disaster-related additional coverage"
                      className="hover:cursor-pointer"
                    >
                      Other disaster-related additional coverage
                    </option>
                    <option
                      value="I did not have additional insurance"
                      className="hover:cursor-pointer"
                    >
                      I did not have additional insurance
                    </option>
                  </>
                ) : name === "automotive_insurance_type" ? (
                  <>
                    <option
                      value="Full Coverage"
                      className="hover:cursor-pointer"
                    >
                      Full Coverage
                    </option>
                    <option value="Liability" className="hover:cursor-pointer">
                      Liability
                    </option>
                  </>
                ) : (
                  <option value="" disabled>
                    {placeholder}
                  </option>
                )}
              </select>
            ) : (
              <input
                className="w-full bg-white text-left  text-xs text-black [border:none] [outline:none]"
                placeholder={placeholder}
                name={name}
                value={tempHouseholdFormData[name] || ""}
                onChange={
                  name === "annual_income"
                    ? handleAnnualIncomeChange
                    : handleHouseholdInputChange
                }
                onBlur={
                  name === "annual_income"
                    ? validateAnnualIncome
                    : handleHouseholdInputBlur
                }
              />
            )
          ) : (
            <div
              className={`flex-1  text-xs ${
                householdFormData[name] !== undefined &&
                householdFormData[name] !== ""
                  ? "font-semibold text-gray-800"
                  : "text-gray-500"
              } text-left`}
              additional_insurance_type
            >
              {name === "insurance"
                ? householdFormData[name]
                  ? "Yes"
                  : "No"
                : householdFormData[name] || placeholder}
            </div>
          )}
        </div>
        {isHouseholdEditMode && validationErrors[name] && (
          <div className="mt-1 text-xs text-red-500">
            {validationErrors[name]}
          </div>
        )}
      </div>
    );
  };

  const [isHouseholdMembersVisible, setIsHouseholdMembersVisible] =
    useState(true);
  // const [lastHouseholdMembersEditedTime, setLastHouseholdMembersEditedTime] =
  //   useState(null);

  const toggleHouseholdMembers = (event) => {
    event.preventDefault();
    setIsHouseholdMembersVisible(!isHouseholdMembersVisible);
  };

  const gridRef = useRef(null);

  const checkForDuplicates = useCallback(
    async (memberData) => {
      if (!data && !data?.household_id) return false;
      try {
        const existingMembers = await citizenProfileService.getHouseholdMembers(
          data?.household_id
        );

        if (memberData.email) {
          return existingMembers.some(
            (member) =>
              !member.is_deleted &&
              member.email &&
              member.email.toLowerCase() === memberData.email.toLowerCase()
          );
        } else {
          return existingMembers.some(
            (member) =>
              !member.is_deleted &&
              member.first_name.toLowerCase() ===
                memberData.first_name.toLowerCase() &&
              member.last_name.toLowerCase() ===
                memberData.last_name.toLowerCase()
          );
        }
      } catch (error) {
        console.error("Error checking for duplicates:", error);
        return false;
      }
    },
    [data]
  );

  // Filter household members to only include those with status true
  const filteredHouseholdMembers = useMemo(
    () =>
      Array.isArray(householdMembers)
        ? householdMembers
            .filter((member) => !member.is_deleted)
            .map((member) => ({
              ...member,
              date_of_birth: member.date_of_birth
                ? new Date(member.date_of_birth).toLocaleDateString("en-US", {
                    month: "2-digit",
                    day: "2-digit",
                    year: "numeric",
                  })
                : "mm/dd/yyyy",
            }))
        : [],
    [householdMembers]
  );

  const [pets, setPets] = useState([]);
  const [isPetsVisible, setIsPetsVisible] = useState(true);
  // const [lastPetsEditedTime, setLastPetsEditedTime] = useState(null);
  const petsGridRef = useRef(null);

  const togglePets = () => {
    setIsPetsVisible(!isPetsVisible);
  };

  const filteredPets = useMemo(
    () => (Array.isArray(pets) ? pets.filter((pet) => !pet.is_deleted) : []),
    [pets]
  );

  const {
    register: householdMemberRegister,
    handleSubmit: householdMemberHandleSubmit,
    formState: { errors: householdMemberErrors },
    setError,
    clearErrors,
    reset: householdMemberReset,
    control: householdMemberControl,
  } = useForm({
    defaultValues: {},
  });
  const onSubmitHouseholdMember = useCallback(
    async (values) => {
      if (!isValidDate(values.date_of_birth)) {
        setError("date_of_birth", { message: "Invalid date." });
        return;
      }
      // UPDATE THE USER COLLECTION
      const { id, ...rest } = values;
      // console.log(values);
      const updatedData = {
        ...rest,
        date_of_birth: values.date_of_birth.toLowerCase()
          ? values.date_of_birth.toLowerCase() == "invalid date"
            ? ""
            : values.date_of_birth
          : "",
        email: values.email ? values.email.toLowerCase() : "",
      };
      setIsSubmittingHouseholdMember(true);

      if (isEditingHouseholdMember) {
        const householdMemberRef = doc(
          db,
          `household/${data?.household_id}/household_members/${id}`
        );
        const householdMemberRefData = await getDoc(householdMemberRef);

        if (householdMemberRefData.exists()) {
          await updateDoc(householdMemberRef, {
            ...updatedData,
            updated_at: serverTimestamp(),
          });
        } else {
          await setDoc(householdMemberRef, {
            ...updatedData,
            is_deleted: false,
            created_at: serverTimestamp(),
            updated_at: serverTimestamp(),
          });
        }
      } else {
        const formattedData = {
          ...updatedData,
          email: updatedData.email.toLowerCase(),
        };
        const isDuplicate = await checkForDuplicates(formattedData);
        if (isDuplicate) {
          DialogUtility.alert({
            title: "Duplicate Entry",
            content: formattedData.email
              ? "A household member with this email already exists."
              : "A household member with this name already exists.",
            showCloseIcon: true,
            closeOnEscape: true,
            position: { X: "center", Y: "center" },
          });
          setIsSubmittingHouseholdMember(false);

          return;
        }
        if (formattedData.email) {
          const usersRef = collection(db, "users");
          const q = query(usersRef, where("email", "==", formattedData.email));
          const querySnapshot = await getDocs(q);

          if (
            !querySnapshot.empty &&
            querySnapshot.docs[0].data().email !== data.email
          ) {
            const userData = querySnapshot.docs[0].data();

            if (
              userData.household_id &&
              userData.household_id !== data?.household_id
            ) {
              DialogUtility.alert({
                title: "User Already in Household",
                content: "This user is already part of another household.",
                showCloseIcon: true,
                closeOnEscape: true,
                position: { X: "center", Y: "center" },
              });
              setIsSubmittingHouseholdMember(false);
              return;
            }
          }
        }

        // PROCEED TO ADD THE HOUSEHOLD MEMBER
        const household_member_id = uuidv4();
        const householdMemberPath = doc(
          db,
          `household/${data?.household_id}/household_members/${household_member_id}`
        );
        await setDoc(householdMemberPath, {
          ...formattedData,
          household_member_id,
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
          is_deleted: false,
        });
      }

      await fetchHouseholdMembers();

      setActiveMenuHouseholdMember(false);
      householdMemberReset();
      setIsSubmittingHouseholdMember(false);
      setIsEditingHouseholdMember(false);
      setShowHouseholdMemberModal(false);
      // setSelectedHouseholdMember(null);
    },
    [
      checkForDuplicates,
      data,
      fetchHouseholdMembers,
      householdMemberReset,
      isEditingHouseholdMember,
      setError,
    ]
  );

  const {
    register: petsRegister,
    handleSubmit: petsHandleSubmit,
    formState: { errors: petsErrors },
    reset: petsReset,
    control: petsControl,
    setValue: petsSetValue,
    watch: petsWatch,
  } = useForm({
    defaultValues: {},
  });
  const onSubmitPets = useCallback(
    async (values) => {
      // UPDATE THE USER COLLECTION
      const { id, is_deleted, ...rest } = values;
      const updatedData = {
        ...rest,
      };
      setIsSubmittingPets(true);

      // console.log("values", values);

      if (isEditingPets) {
        const householdPetRef = doc(
          db,
          `household/${data?.household_id}/pets/${id}`
        );
        const householdPetRefData = await getDoc(householdPetRef);

        if (householdPetRefData.exists()) {
          await updateDoc(householdPetRef, {
            ...updatedData,
            updated_at: serverTimestamp(),
          });
        } else {
          await setDoc(householdPetRef, {
            ...updatedData,
            is_deleted: false,
            created_at: serverTimestamp(),
            updated_at: serverTimestamp(),
          });
        }
      } else {
        const formattedData = {
          ...updatedData,
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
          is_deleted: false,
        };
        // PROCEED TO ADD THE HOUSEHOLD MEMBER
        const householdPetId = uuidv4();
        const householdPetPath = doc(
          db,
          `household/${data?.household_id}/pets/${householdPetId}`
        );
        await setDoc(householdPetPath, {
          ...formattedData,
        });
      }

      await fetchPets();

      setActiveMenuPets(false);
      petsReset();
      setIsSubmittingPets(false);
      setIsEditingPets(false);
      setShowPetsModal(false);
    },
    [data, isEditingPets, petsReset]
  );
  const petsValues = petsWatch();

  const onClosePetPhotosModal = () => {
    setShowPetPhotosModal(false);
    setSelectedPetId(null);
    setSelectedPet({});
  };
  const handlePetPhotosCollectionDelete = useCallback(
    async (newData) => {
      setIsSubmittingPets(true);
      setSelectedPet({
        pet_image: newData,
      });

      const householdPetRef = doc(
        db,
        `household/${data?.household_id}/pets/${selectedPetId}`
      );

      const householdPetRefData = await getDoc(householdPetRef);

      if (householdPetRefData.exists()) {
        await updateDoc(householdPetRef, {
          pet_image: newData,
          updated_at: serverTimestamp(),
        });
      }
      await fetchPets();
      setIsSubmittingPets(false);
    },
    [selectedPetId, fetchPets, data]
  );
  const handlePetPhotosCollectionUpdate = useCallback(
    async (newFile) => {
      const householdPetRef = doc(
        db,
        `household/${data?.household_id}/pets/${selectedPetId}`
      );

      const householdPetRefData = await getDoc(householdPetRef);

      if (householdPetRefData.exists()) {
        const existingPetImages = householdPetRefData.data()?.pet_image || [];
        existingPetImages.push(newFile);
        const householdPetNewData = {
          pet_image: existingPetImages,
        };
        await updateDoc(householdPetRef, householdPetNewData);
        // console.log("householdPetRefData exists");
      }

      await fetchPets();
    },
    [data, selectedPetId, fetchPets]
  );

  const handleEditFilePetPhotos = useCallback(
    (newData) => {
      setIsEditingPets(true);
      setShowPetsModal(true);
      setActiveMenuPets(null);
      const latestPetData = pets?.filter((pet) => pet?.id === selectedPetId)[0];
      petsReset({
        ...latestPetData,
        pet_image: newData,
      });
    },
    [pets, petsReset, selectedPetId]
  );

  const renderPetModal = useMemo(() => {
    return (
      <ViewPhotosModal
        isOpen={showPetPhotosModal}
        onClose={() => {
          onClosePetPhotosModal();
        }}
        photosData={selectedPet?.pet_image || []}
        storagePath={`household/pets/${selectedUserId}`}
        handleCollectionUpdate={handlePetPhotosCollectionUpdate}
        dateKey="date_inserted"
        onEditClick={(newData) => {
          // console.log("edit clicked");
          onClosePetPhotosModal();
          handleEditFilePetPhotos(newData);
        }}
        isLoadingDelete={isSubmittingPets}
        handleDelete={handlePetPhotosCollectionDelete}
      />
    );
  }, [
    showPetPhotosModal,
    selectedPet,
    selectedUserId,
    handlePetPhotosCollectionUpdate,
    isSubmittingPets,
    handlePetPhotosCollectionDelete,
    handleEditFilePetPhotos,
  ]);

  const renderPetsModal = useMemo(() => {
    return (
      <Modal
        isOpen={showPetsModal}
        title={isEditingPets ? "Edit Pet" : "Add New Pet"}
        onClose={() => {
          setShowPetsModal(false);
          setActiveMenuPets(false);
          petsReset();
          // setSelectedHouseholdMember(null);
        }}
        formRef={petsFormRef}
        isLoading={isSubmittingPets}
        isEdit={isEditingPets}
      >
        <form
          onSubmit={petsHandleSubmit(onSubmitPets)}
          className="grid grid-cols-4 gap-4"
          ref={petsFormRef}
        >
          <div className=" col-span-4 ">
            <SimpleFileUploader
              storagePath={`household/pets/${selectedUserId}`}
              onFileUpload={(photoUrls) => {
                if (photoUrls) {
                  petsSetValue("pet_image", photoUrls);
                }
              }}
              initialPhoto={petsValues?.pet_image}
              acceptedTypes="image/*"
              className="mb-4"
              dateKey="date_inserted"
            />
          </div>
          <div className="col-span-2">
            <CustomInput
              type="text"
              name="pet_name"
              placeholder="Name"
              register={{
                ...petsRegister("pet_name", {
                  required: "Name is required",
                  pattern: {
                    value: alphabetRegex,
                    message: "Numbers are not allowed in name",
                  },
                }),
              }}
              errors={petsErrors}
            />
          </div>
          <div className="col-span-2">
            <CustomInput
              type="text"
              name="pet_breed"
              placeholder="Breed"
              register={{
                ...petsRegister("pet_breed", {
                  // required: "Breed is required",
                  pattern: {
                    value: alphabetRegex,
                    message: "Numbers are not allowed in breed",
                  },
                }),
              }}
              errors={petsErrors}
            />
          </div>
          <div className="col-span-2">
            <CustomInput
              type="text"
              name="pet_color"
              placeholder="Color"
              register={{
                ...petsRegister("pet_color", {
                  // required: "Color is required",
                  pattern: {
                    value: alphabetRegex,
                    message: "Numbers are not allowed in color",
                  },
                }),
              }}
              errors={petsErrors}
            />
          </div>
          <div className="col-span-2">
            <CustomSelect
              name="pet_coat"
              placeholder="Coat"
              register={{
                ...petsRegister("pet_coat", {
                  // required: "Coat is required",
                }),
              }}
              options={PetsCoatOptions}
              errors={petsErrors}
            />
          </div>
          <div className="col-span-2">
            <CustomSelect
              name="pet_size"
              placeholder="Size"
              register={{
                ...petsRegister("pet_size", {
                  // required: "Size is required",
                }),
              }}
              options={PetsSizeOptions}
              errors={petsErrors}
            />
          </div>
        </form>
      </Modal>
    );
  }, [
    showPetsModal,
    isEditingPets,
    isSubmittingPets,
    petsHandleSubmit,
    onSubmitPets,
    selectedUserId,
    petsValues,
    petsRegister,
    petsErrors,
    petsReset,
    petsSetValue,
  ]);

  const renderDeletePetModal = useMemo(() => {
    return (
      <DeleteModal
        isOpen={showDeletePetModal}
        onClose={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setShowDeletePetModal(false);
          setPetsToDeleteId(null);
        }}
        onDelete={async (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!data?.household_id) return;
          setIsSubmittingPets(true);

          const householdPetRef = doc(
            db,
            `household/${data?.household_id}/pets/${petsToDeleteId}`
          );

          const householdPetRefData = await getDoc(householdPetRef);

          if (householdPetRefData.exists()) {
            await updateDoc(householdPetRef, {
              is_deleted: true,
              updated_at: serverTimestamp(),
            });
          }

          setShowDeletePetModal(false);
          setIsSubmittingPets(false);
          setPetsToDeleteId(null);
          await fetchPets();
        }}
        itemName="Pet"
        loading={isSubmittingPets}
      />
    );
  }, [showDeletePetModal, isSubmittingPets, data, petsToDeleteId, fetchPets]);

  const onDeleteHouseholdMember = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsSubmittingHouseholdMember(true);
      // const disasterId = selectedDisasters[0];
      const householdMemberRef = doc(
        db,
        `household/${data?.household_id}/household_members/${householdMemberToDeleteId}`
      );

      const householdMemberRefData = await getDoc(householdMemberRef);

      if (householdMemberRefData.exists()) {
        await updateDoc(householdMemberRef, {
          is_deleted: true,
          updated_at: serverTimestamp(),
        });
      }

      setShowDeleteModal(false);
      setIsSubmittingHouseholdMember(false);
      await fetchHouseholdMembers(data);
    },
    [data, fetchHouseholdMembers, householdMemberToDeleteId]
  );

  const renderDeleteHouseholdMemberModal = useMemo(() => {
    return (
      <DeleteModal
        isOpen={showDeleteModal}
        onClose={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setShowDeleteModal(false);
          setHouseholdMemberToDeleteId(null);
        }}
        isDelete={true}
        onDelete={onDeleteHouseholdMember}
        itemName="Household Member"
        loading={isSubmittingHouseholdMember}
      />
    );
  }, [showDeleteModal, onDeleteHouseholdMember, isSubmittingHouseholdMember]);

  const renderInviteHouseholdMemberModal = useMemo(() => {
    return (
      <DeleteModal
        isOpen={showInviteModal}
        onClose={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setShowInviteModal(false);
          setSelectedInviteHouseholdMember(null);
        }}
        isDelete={false}
        onDelete={async () => {
          if (
            selectedInviteHouseholdMember?.email &&
            !selectedInviteHouseholdMember?.acc_created
          ) {
            setIsSubmittingHouseholdMember(true);
            const docRef = await addDoc(collection(db, "invited_users"), {
              invited_by: data.id,
              sent_to: selectedInviteHouseholdMember?.email,
              date_invited: serverTimestamp(),
              first_name: helpers.capitalizeFirstLetter(
                selectedInviteHouseholdMember?.first_name
              ),
              last_name: helpers.capitalizeFirstLetter(
                selectedInviteHouseholdMember?.last_name
              ),
              type: "household_member",
              acc_created: false,
            });

            // Update the document to include its ID
            await updateDoc(docRef, {
              id: docRef.id,
            });

            //const link = `${registrationLink}/${invitedUser.id}/${user.id}/${user?.household_id}/${householdData?.household_member_id}/${params?.id}`;

            const link = `${window.location.origin}/registration/${docRef.id}/${data.id}/${data?.household_id}/${data?.household_member_id}`;
            try {
              await mailer.sendHouseholdMemberInviteEmail(
                selectedInviteHouseholdMember?.email,
                helpers.capitalizeFirstLetter(
                  selectedInviteHouseholdMember?.first_name
                ),
                link,
                helpers.capitalizeFirstLetter(data.first_name)
              );
              setIsSubmittingHouseholdMember(false);
              setShowInviteModal(false);
              setSelectedInviteHouseholdMember(null);
            } catch (error) {
              setIsSubmittingHouseholdMember(false);
              console.error("Error sending email:", error);
            }
          }
        }}
        title="Invite Household Member"
        description="Are you sure you want to invite this household member?"
        itemName="Household Member"
        loading={isSubmittingHouseholdMember}
      />
    );
  }, [
    showInviteModal,
    isSubmittingHouseholdMember,
    selectedInviteHouseholdMember?.email,
    selectedInviteHouseholdMember?.acc_created,
    selectedInviteHouseholdMember?.first_name,
    selectedInviteHouseholdMember?.last_name,
    data,
  ]);

  const renderHouseholdMembersModal = useMemo(() => {
    return (
      <Modal
        isOpen={showHouseholdMemberModal}
        title={
          isEditingHouseholdMember
            ? "Edit Household Member"
            : "Add New Household Member"
        }
        onClose={() => {
          setShowHouseholdMemberModal(false);
          setActiveMenuHouseholdMember(false);
          householdMemberReset();
        }}
        formRef={householdMemberFormRef}
        isLoading={isSubmittingHouseholdMember}
        isEdit={isEditingHouseholdMember}
      >
        <form
          onSubmit={householdMemberHandleSubmit(onSubmitHouseholdMember)}
          className="grid grid-cols-4 gap-4"
          ref={householdMemberFormRef}
        >
          <div className="col-span-2">
            <CustomInput
              type="text"
              name="first_name"
              placeholder="First Name"
              register={{
                ...householdMemberRegister("first_name", {
                  required: "First Name is required",
                  pattern: {
                    value: alphabetRegex,
                    message: "Numbers are not allowed in names",
                  },
                }),
              }}
              errors={householdMemberErrors}
            />
          </div>
          <div className="col-span-2">
            <CustomInput
              type="text"
              name="last_name"
              placeholder="Last Name"
              register={{
                ...householdMemberRegister("last_name", {
                  required: "Last Name is required",
                  pattern: {
                    value: alphabetRegex,
                    message: "Numbers are not allowed in names",
                  },
                }),
              }}
              errors={householdMemberErrors}
            />
          </div>
          <div className="col-span-2">
            <Controller
              control={householdMemberControl}
              name="date_of_birth"
              render={({ field }) => {
                return (
                  <DatePickerTemplate
                    label="Date of Birth"
                    labelClassName="text-sm"
                    className="px-2"
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(e);
                      clearErrors("date_of_birth");
                    }}
                    errors={householdMemberErrors}
                    name="date_of_birth"
                  />
                );
              }}
            />
          </div>

          <div className="col-span-2">
            <CustomSelect
              name="relationship"
              placeholder="Relationship"
              options={RelationshipOptions}
              register={{
                ...householdMemberRegister("relationship", {
                  // required: "Relationship is required",
                }),
              }}
              errors={householdMemberErrors}
            />
          </div>
          <div className="col-span-4">
            <CustomInput
              type="text"
              name="email"
              placeholder="Email Address"
              register={{
                ...householdMemberRegister("email", {
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Invalid email address",
                  },
                  validate: {
                    validEmail: (value) => {
                      if (!value || value.trim() === "") {
                        return;
                      }
                      const emailRegex =
                        /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
                      return emailRegex.test(value) || "Invalid email address";
                    },
                  },
                }),
              }}
              errors={householdMemberErrors}
            />
          </div>
          <div className="col-span-2">
            <CustomSelect
              name="gender"
              placeholder="Gender"
              options={GenderOptions}
              register={{
                ...householdMemberRegister("gender", {
                  required: "Gender is required",
                }),
              }}
              errors={householdMemberErrors}
            />
          </div>

          <div className="col-span-2">
            <CustomSelect
              name="ethnicity"
              placeholder="Ethnicity"
              options={EthnicityOptions}
              register={{
                ...householdMemberRegister("ethnicity", {
                  required: "Ethnicity is required",
                }),
              }}
              errors={householdMemberErrors}
            />
          </div>
          <div className="col-span-2">
            <CustomSwitch
              rowType={true}
              label="Do you have a disability?"
              control={householdMemberControl}
              name="disability"
              // rules={{
              //   required: "This field is required",
              //   validate: (value) => value === true || "Must be checked",
              // }}
              errors={householdMemberErrors}
            />
          </div>
          <div className="col-span-2">
            <CustomSwitch
              rowType={true}
              label="Are you Active Military / Vet?"
              control={householdMemberControl}
              name="military_vet"
              // rules={{
              //   required: "This field is required",
              //   validate: (value) => value === true || "Must be checked",
              // }}
              errors={householdMemberErrors}
            />
          </div>
        </form>
      </Modal>
    );
  }, [
    clearErrors,
    householdMemberControl,
    householdMemberErrors,
    householdMemberHandleSubmit,
    householdMemberRegister,
    householdMemberReset,
    isEditingHouseholdMember,
    isSubmittingHouseholdMember,
    onSubmitHouseholdMember,
    showHouseholdMemberModal,
  ]);

  const renderPetsGrid = useMemo(() => {
    return (
      <div className="flex flex-col gap-4 self-stretch">
        <GridComponent
          // ref={petsGridRef}
          dataSource={filteredPets}
          keyField="pet_id"
          allowSorting={true}
          sortSettings={sortSettings}
          // editSettings={editing}
          filterSettings={filterSettings}
          allowFiltering={true}
          // actionBegin={actionBeginPets}
          // actionComplete={petsActionComplete}
          allowPaging={true}
          pageSettings={pageSettings}
          showColumnMenu={true}
          columnMenuItems={columnMenuItems}
          cssClass="new-grid-color allow-overflow-grid"
        >
          <ColumnsDirective>
            <ColumnDirective
              field="pet_id"
              headerText="Pet ID"
              isPrimaryKey={true}
              visible={false}
            />
            <ColumnDirective
              field="pet_name"
              headerText="Pet Name"
              width="100"
              // textAlign="Center"
              validationRules={{ required: true }}
            />
            <ColumnDirective
              field="pet_color"
              headerText="Pet Color"
              width="100"
              // textAlign="Center"
              validationRules={{ required: true }}
            />
            <ColumnDirective
              field="pet_coat"
              headerText="Pet Coat"
              width="100"
              // textAlign="Center"
              validationRules={{ required: true }}
              editType="dropdownedit"
              edit={petCoatParams}
            />
            <ColumnDirective
              field="pet_breed"
              headerText="Pet Breed"
              width="100"
              // textAlign="Center"
              validationRules={{ required: true }}
            />
            <ColumnDirective
              field="pet_size"
              headerText="Pet Size"
              width="100"
              // textAlign="Center"
              validationRules={{ required: true }}
              editType="dropdownedit"
              edit={petSizeParams}
            />
            <ColumnDirective
              field="pet_image"
              headerText="Photos"
              width="100"
              template={(props) => {
                return (
                  <PhotoColumn
                    data={props.pet_image}
                    onClick={() => {
                      // if (!props.pet_image || !props.pet_image.length) {
                      //   return;
                      // }
                      const { column, index, foreignKeyData, ...rest } = props;

                      setShowPetPhotosModal(true);
                      setSelectedPet(rest);
                      setSelectedPetId(props?.id || "");
                    }}
                  />
                );
              }}
              // edit={PhotosEdit}
            />
            <ColumnDirective
              headerText="Actions"
              width="40"
              textAlign="Center"
              template={(props) => {
                return (
                  <div className="flex w-full justify-center overflow-visible px-4">
                    <ThreeDotMenu
                      menuId={props?.id}
                      activeMenuId={activeMenuPets}
                      setActiveMenuId={setActiveMenuPets}
                      clickOutside={false}
                      onEditClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        // setSelectedHouseholdMember(props);
                        const { column, index, foreignKeyData, ...rest } =
                          props;
                        setIsEditingPets(true);
                        setShowPetsModal(true);
                        setActiveMenuPets(null);
                        petsReset({
                          ...rest,
                        });
                      }}
                      onDeleteClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowDeletePetModal(true);
                        setActiveMenuPets(null);
                        setPetsToDeleteId(props?.id);
                      }}
                    />
                  </div>
                );
              }}
            />
          </ColumnsDirective>
          <Inject
            services={[
              Page,
              // Edit,
              Sort,
              Filter,
              CommandColumn,
              ColumnMenu,
              Aggregate,
            ]}
          />
        </GridComponent>
      </div>
    );
  }, [activeMenuPets, filteredPets, petsReset]);

  const renderHouseholdMembersGrid = useMemo(() => {
    return (
      <div className="flex flex-col gap-4 self-stretch">
        <GridComponent
          // ref={gridRef}
          dataSource={filteredHouseholdMembers}
          allowSorting={true}
          sortSettings={sortSettings}
          // editSettings={editing}
          filterSettings={filterSettings}
          allowFiltering={true}
          // actionBegin={actionBegin}
          // actionComplete={actionComplete}
          allowPaging={true}
          pageSettings={pageSettings}
          showColumnMenu={true}
          columnMenuItems={columnMenuItems}
          cssClass="new-grid-color allow-overflow-grid allow-overflow-grid-text"
          // emptyRecordTemplate={emptyRecordTemplate}
          // aggregates={[
          //   {
          //     columns: [
          //       {
          //         type: "Custom",
          //         customAggregate: () => "",
          //         footerTemplate: () => (
          //           <div
          //             className="cursor-pointer bg-white py-2 pl-2 text-blue-600 hover:underline"
          //             onClick={handleAddClick}
          //           >
          //             + Add New Household Member
          //           </div>
          //         ),
          //         field: "first_name",
          //       },
          //     ],
          //   },
          // ]}
        >
          <ColumnsDirective>
            <ColumnDirective
              field="acc_created"
              headerText="householdMemberID"
              visible={false}
            />
            <ColumnDirective
              field="household_member_id"
              headerText="householdMemberID"
              isPrimaryKey={true}
              visible={false}
            />
            <ColumnDirective
              field="Name"
              headerText="Name"
              width="160"
              validationRules={{ required: true }}
              template={(props) => {
                return (
                  <div>
                    {props?.first_name} {props?.last_name}
                  </div>
                );
              }}
            />

            {/* <ColumnDirective
                    field="first_name"
                    headerText="First Name"
                    width="100"
                    validationRules={{ required: true }}
                  />
                  <ColumnDirective
                    field="last_name"
                    headerText="Last Name"
                    width="100"
                    validationRules={{ required: true }}
                  /> */}
            <ColumnDirective
              field="email"
              headerText="Email"
              width="160"
              // validationRules={{ required: true }}
            />

            <ColumnDirective
              field="date_of_birth"
              headerText="Date of Birth"
              width="100"
              template={(props) => {
                const dob = props?.date_of_birth;
                return <div>{dob && dob !== "Invalid Date" ? dob : ""}</div>;
              }}
            />
            <ColumnDirective
              field="relationship"
              headerText="Relationship"
              width="100"
              validationRules={{ required: true }}
              // editType="dropdownedit"
              // edit={relationshipParams}
            />
            <ColumnDirective
              field="gender"
              headerText="Gender"
              width="100"
              validationRules={{ required: true }}
            />
            <ColumnDirective
              field="ethnicity"
              headerText="Ethnicity"
              width="100"
              validationRules={{ required: true }}
            />
            <ColumnDirective
              field="disability"
              headerText="Person with disability?"
              width="120"
              validationRules={{ required: true }}
              template={(props) => <div>{props.disability ? "Yes" : "No"}</div>}
            />
            <ColumnDirective
              field="military_vet"
              headerText="Active Military/ Vet"
              width="100"
              // editType="dropdownedit"
              // edit={militaryvetParams}
              validationRules={{ required: true }}
              template={(props) => (
                <div>{props.military_vet ? "Yes" : "No"}</div>
              )}
            />
            <ColumnDirective
              headerText="Actions"
              textAlign="Center"
              width="80"
              template={(props) => {
                return (
                  <div className="flex w-full justify-center overflow-visible px-4">
                    <ThreeDotMenu
                      menuId={props?.household_member_id}
                      activeMenuId={activeMenuHouseholdMember}
                      setActiveMenuId={setActiveMenuHouseholdMember}
                      clickOutside={false}
                      onEditClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        // setSelectedHouseholdMember(props);
                        const { column, index, foreignKeyData, ...rest } =
                          props;
                        setIsEditingHouseholdMember(true);
                        setShowHouseholdMemberModal(true);
                        setActiveMenuHouseholdMember(null);
                        householdMemberReset({
                          first_name: props?.first_name || "",
                          last_name: props?.last_name || "",
                          email: props?.email || "",
                          date_of_birth: props?.date_of_birth || "",
                          relationship: props?.relationship || "",
                          disability: props?.disability || "",
                          military_vet: props?.military_vet || "",
                          gender: props?.gender || "",
                          ethnicity: props?.ethnicity || "",
                          id: props?.id || "",
                          household_member_id: props?.household_member_id || "",
                          ...rest,
                        });
                      }}
                      onDeleteClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowDeleteModal(true);
                        setActiveMenuHouseholdMember(null);
                        setHouseholdMemberToDeleteId(props?.id);
                      }}
                      customItems={
                        !props?.acc_created
                          ? [
                              {
                                label: "Invite",
                                onClick: (e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setShowInviteModal(true);
                                  setSelectedInviteHouseholdMember(props);
                                  setActiveMenuHouseholdMember(null);
                                  // handleCommandClick(props);
                                },
                              },
                            ]
                          : []
                      }
                    />
                  </div>
                );
              }}
            />
          </ColumnsDirective>
          <Inject
            services={[
              Page,
              // Edit,
              Sort,
              Filter,
              CommandColumn,
              ColumnMenu,
              Aggregate,
            ]}
          />
        </GridComponent>
      </div>
    );
  }, [
    activeMenuHouseholdMember,
    filteredHouseholdMembers,
    householdMemberReset,
  ]);

  // Add new state for completion modal
  const [showCompletionModal, setShowCompletionModal] = useState(false);

  return (
    <>
      {/* PET VIEW PHOTOS MODAL */}
      {renderPetModal}
      {/* PETS MODAL */}
      {renderPetsModal}
      {/* PET DELETE MODAL */}
      {renderDeletePetModal}
      {/* DELETE HOUSEHOLD MEMBER MODAL */}
      {renderDeleteHouseholdMemberModal}
      {/* INVITE HOUSEHOLD MEMBER MODAL */}
      {renderInviteHouseholdMemberModal}
      {/* HOUSEHOLD MEMBERS MODAL */}
      {renderHouseholdMembersModal}
      <div
        className={`m-0 box-border flex w-[1648px] max-w-full flex-col items-start justify-start px-2.5 py-[15px] leading-[normal] tracking-[normal]`}
      >
        <section className="flex max-w-full flex-col items-start justify-start self-stretch bg-white">
          <header
            className="mb-2 flex flex-row items-center justify-between self-stretch rounded bg-[#003460] p-2"
            id="personalinfo"
          >
            <div>
              <h2 className="text-xl font-bold text-white">
                Personal Information
              </h2>
            </div>
            <div className="flex items-center">
              <button
                type="button"
                className="flex h-4 w-4 cursor-pointer items-center justify-center  bg-[#003460] text-white transition-all duration-300 ease-in-out"
                onClick={togglePersonalInfo}
              >
                <span
                  className={`transition-transform duration-300 ease-in-out ${
                    isPersonalInfoVisible ? "rotate-0" : "rotate-180"
                  }`}
                >
                  <img src={toggleDown} alt="down" className="h-8 w-8" />
                </span>
              </button>
            </div>
          </header>
          {isPersonalInfoVisible && (
            <div className="flex flex-col gap-4 self-stretch px-2">
              <div className="flex flex-row gap-4">
                {renderField("First Name", "first_name", "", false, "flex-1")}
                {renderField("Last Name", "last_name", "", false, "flex-1")}
                {renderField(
                  "Date of Birth",
                  "birth_date",
                  "MM/DD/YYYY",
                  false,
                  "flex-1",
                  isEditMode ? { border: "none", paddingLeft: 0 } : {}
                )}
              </div>

              {renderField("Address 1", "address1", "")}
              {renderField("Address 2", "address2", "")}
              <div className="flex flex-row gap-4">
                {renderField("City", "city", "", false, "flex-1")}
                {renderField(
                  "Select a state",
                  "state",
                  "Select a state",
                  true,
                  "flex-1"
                )}
                {renderField("Zip", "zip", "e.g., 62704", false, "flex-1")}
                {renderField(
                  "County",
                  "county",
                  "Select a county",
                  true,
                  "flex-1"
                )}
              </div>
              <div className="flex flex-row gap-4">
                {renderField("Email", "email", "", false, "flex-1")}
                {renderField(
                  "Phone Number",
                  "mobile_number",
                  "",
                  false,
                  "flex-1"
                )}
                {renderField(
                  "Alternate Phone Number",
                  "alternate_phone",
                  "",
                  false,
                  "flex-1"
                )}
              </div>
              <div className="flex flex-row gap-4">
                {/* {renderField(
                  "Citizenship",
                  "citizenship",
                  "Citizenship",
                  true,
                  "flex-1"
                )} */}
                {renderField(
                  "Ethnicity",
                  "ethnicity",
                  "Ethnicity",
                  true,
                  "flex-1"
                )}
                {renderField("Sex", "gender", "Sex", true, "flex-1")}
              </div>
              <header className="mt-2 flex flex-row items-center justify-between self-stretch">
                <div>
                  <h2 className="font-Inter text-base font-bold text-[#003460]">
                    Alternate Contact
                  </h2>
                </div>
              </header>
              <div className="flex flex-row gap-4">
                {renderField(
                  "Contact Name",
                  "alternate_contact_name",
                  "",
                  false,
                  "flex-1"
                )}
                {renderField("Email", "alternate_email", "", false, "flex-1")}
                {renderField(
                  "Phone Number",
                  "alternate_mobile_number",
                  "",
                  false,
                  "flex-1"
                )}
              </div>
              <div className="mb-2 flex flex-row gap-2">
                <div className="w-auto">
                  <p className="text-gray-600 mb-1 pb-3 pt-1 text-xs">
                    Do you have a disability?
                    {isEditMode && (
                      <span
                        className="ml-1 text-red-500"
                        style={{ fontSize: "26px" }}
                      >
                        *
                      </span>
                    )}
                  </p>
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="disability"
                        value="Yes"
                        checked={tempHouseholdFormData.disability === true}
                        onChange={(e) => {
                          handleHouseholdInputChange(e);
                          handleInputBlur();
                        }}
                        disabled={!isEditMode}
                        className={`mr-2 ${
                          !isEditMode ? "" : "cursor-pointer"
                        }`}
                      />
                      <span className="text-xs">Yes</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="disability"
                        value="No"
                        checked={tempHouseholdFormData.disability === false}
                        onChange={(e) => {
                          handleHouseholdInputChange(e);
                          handleInputBlur();
                        }}
                        disabled={!isEditMode}
                        className={`mr-2 ${
                          !isEditMode ? "" : "cursor-pointer"
                        }`}
                      />
                      <span className="text-xs">No</span>
                    </label>
                  </div>
                  {validationErrors.disability && (
                    <div className="mt-1 text-xs text-red-500">
                      {validationErrors.disability}
                    </div>
                  )}
                </div>

                <div className="w-auto" style={{ marginLeft: "50px" }}>
                  <p className="text-gray-600 mb-1 pb-3 pt-1 text-xs">
                    Are you Active Military / Vet?
                    {isEditMode && (
                      <span
                        className="ml-1 text-red-500"
                        style={{ fontSize: "26px" }}
                      >
                        *
                      </span>
                    )}
                  </p>
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="military_vet"
                        value="Yes"
                        checked={tempHouseholdFormData.military_vet === true}
                        onChange={(e) => {
                          handleHouseholdInputChange(e);
                          handleInputBlur();
                        }}
                        disabled={!isEditMode}
                        className={`mr-2 ${
                          !isEditMode ? "" : "cursor-pointer"
                        }`}
                      />
                      <span className="text-xs">Yes</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="military_vet"
                        value="No"
                        checked={tempHouseholdFormData.military_vet === false}
                        onChange={(e) => {
                          handleHouseholdInputChange(e);
                          handleInputBlur();
                        }}
                        disabled={!isEditMode}
                        className={`mr-2 ${
                          !isEditMode ? "" : "cursor-pointer"
                        }`}
                      />
                      <span className="text-xs">No</span>
                    </label>
                  </div>
                  {validationErrors.military_vet && (
                    <div className="mt-1 text-xs text-red-500">
                      {validationErrors.military_vet}
                    </div>
                  )}
                </div>
              </div>
              {isEditMode ? (
                <>
                  <div className="mt-2 flex w-full gap-2">
                    <button
                      type="button"
                      className="flex cursor-pointer gap-1 bg-transparent text-base text-dark-midnight-blue"
                      onClick={saveChanges}
                    >
                      <FaSave className="h-4 w-4" />
                      Save
                    </button>
                    <button
                      type="button"
                      className="flex cursor-pointer gap-1 bg-transparent text-base text-[#9c1717]"
                      onClick={cancelEditing}
                    >
                      <FaTimes className="h-4 w-4" />
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <div className="flex w-full">
                  <button
                    type="button"
                    className=" flex cursor-pointer gap-1 bg-transparent text-base text-dark-midnight-blue"
                    onClick={startEditing}
                  >
                    <FaEdit className="h-4 w-4" />
                    Edit Personal Information
                  </button>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Household Information */}
        <section className="mt-4 flex max-w-full flex-col items-start justify-start self-stretch bg-white">
          <header
            className="mb-2 flex flex-row items-center justify-between self-stretch rounded bg-[#003460] p-2"
            id="householdinfo"
          >
            <div>
              <h2 className="text-xl font-bold text-white">
                Household Information
              </h2>
            </div>
            <div className="flex items-center">
              <button
                type="button"
                className="flex h-4 w-4 cursor-pointer items-center justify-center  bg-[#003460] text-white transition-all duration-300 ease-in-out"
                onClick={toggleHouseholdInfo}
              >
                <span
                  className={`transition-transform duration-300 ease-in-out ${
                    isHouseholdInfoVisible ? "rotate-0" : "rotate-180"
                  }`}
                >
                  <img src={toggleDown} alt="down" className="h-8 w-8" />
                </span>
              </button>
            </div>
          </header>
          {isHouseholdInfoVisible && (
            <div className="mt-2 flex flex-col gap-2 self-stretch px-2">
              <div className="flex flex-row gap-4 hover:cursor-pointer">
                {renderHouseholdField(
                  "Annual Household Income",
                  "annual_income",
                  "Select Annual Income",
                  true,
                  "flex-1",
                  "number",
                  true // Mark as optional
                )}

                {renderHouseholdField(
                  "Owner or Renter?",
                  "owner_or_renter",
                  "Select Owner or Renter",
                  true,
                  "flex-1"
                )}
                {/* {renderHouseholdField(
                "Do you have insurance?",
                "insurance",
                "Select Yes or No",
                true,
                "flex-1"
              )} */}
                {renderHouseholdField(
                  "Household size",
                  "household_size",
                  "",
                  false,
                  "flex-1"
                )}
              </div>

              <div className="flex w-full flex-row gap-8 pt-3">
                {/* First Column - Home Insurance */}
                <div className="flex-1">
                  <div className="w-full">
                    <p className="text-gray-600 mb-1 pb-3 pt-1 text-xs">
                      Do you have Home Insurance?
                      {isEditMode && (
                        <span
                          className="ml-1 text-red-500"
                          style={{ fontSize: "26px" }}
                        >
                          *
                        </span>
                      )}
                    </p>
                    <div className="flex gap-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="home_insurance"
                          value="Yes"
                          checked={
                            tempHouseholdFormData.home_insurance === true
                          }
                          onChange={(e) => {
                            handleHouseholdInputChange(e);
                            handleInputBlur();
                          }}
                          disabled={!isHouseholdEditMode}
                          className={`mr-2 ${
                            !isHouseholdEditMode ? "" : "cursor-pointer"
                          }`}
                        />
                        <span className="text-xs">Yes</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="home_insurance"
                          value="No"
                          checked={
                            tempHouseholdFormData.home_insurance === false
                          }
                          onChange={(e) => {
                            handleHouseholdInputChange(e);
                            handleInputBlur();
                          }}
                          disabled={!isHouseholdEditMode}
                          className={`mr-2 ${
                            !isHouseholdEditMode ? "" : "cursor-pointer"
                          }`}
                        />
                        <span className="text-xs">No</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Second Column - Automotive Insurance */}
                <div className="flex-1">
                  <div className="w-full">
                    <p className="text-gray-600 mb-1 pb-3 pt-1 text-xs">
                      Do you have Automotive Insurance?
                      {isEditMode && (
                        <span
                          className="ml-1 text-red-500"
                          style={{ fontSize: "26px" }}
                        >
                          *
                        </span>
                      )}
                    </p>
                    <div className="flex gap-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="automotive_insurance"
                          value="Yes"
                          checked={
                            tempHouseholdFormData.automotive_insurance === true
                          }
                          onChange={(e) => {
                            handleHouseholdInputChange(e);
                            handleInputBlur();
                          }}
                          disabled={!isHouseholdEditMode}
                          className={`mr-2 ${
                            !isHouseholdEditMode ? "" : "cursor-pointer"
                          }`}
                        />
                        <span className="text-xs">Yes</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="automotive_insurance"
                          value="No"
                          checked={
                            tempHouseholdFormData.automotive_insurance === false
                          }
                          onChange={(e) => {
                            handleHouseholdInputChange(e);
                            handleInputBlur();
                          }}
                          disabled={!isHouseholdEditMode}
                          className={`mr-2 ${
                            !isHouseholdEditMode ? "" : "cursor-pointer"
                          }`}
                        />
                        <span className="text-xs">No</span>
                      </label>
                    </div>
                  </div>
                </div>
                <div className="flex-1" />
              </div>
              <div className="flex w-full flex-row gap-8 pt-3">
                <div className="flex-1">
                  {tempHouseholdFormData.home_insurance &&
                    renderHouseholdField(
                      "Home Insurance Type",
                      "home_insurance_type",
                      "Select Home Insurance Type",
                      true,
                      "w-full",
                      "dropdownedit",
                      false
                    )}
                </div>
                <div className="flex-1">
                  {renderHouseholdField(
                    "Do you have additional insurance on your home or contents?",
                    "additional_insurance_type",
                    "Select Additional Insurance Type",
                    true,
                    "w-full"
                  )}
                </div>
                <div className="flex-1" />
              </div>
              {isHouseholdEditMode ? (
                <>
                  <div className="mt-4 flex w-full gap-2">
                    <button
                      type="button"
                      className=" flex cursor-pointer gap-1 bg-transparent text-base text-dark-midnight-blue"
                      onClick={saveHouseholdChanges}
                    >
                      <FaSave className="h-4 w-4" />
                      Save
                    </button>
                    <button
                      type="button"
                      className="flex cursor-pointer gap-1 bg-transparent text-base text-[#9c1717]"
                      onClick={cancelHouseholdEditing}
                    >
                      <FaTimes className="h-4 w-4" />
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <div className="mt-4 flex w-full">
                  <button
                    type="button"
                    className=" flex cursor-pointer  gap-1 bg-transparent text-base text-dark-midnight-blue"
                    onClick={startHouseholdEditing}
                  >
                    <FaEdit className="h-4 w-4" />
                    Edit Household Information
                  </button>
                </div>
              )}
            </div>
          )}
        </section>

        <section className="flex max-w-full flex-col items-start justify-start self-stretch bg-white pt-4">
          <header
            className="mb-2 flex flex-row items-center justify-between self-stretch rounded bg-[#003460] p-2"
            id="householdinfo"
          >
            <div>
              <h2 className="text-xl font-bold text-white">
                Additional Household Members
              </h2>
            </div>
            <div className="flex items-center">
              <button
                type="button"
                className="flex h-4 w-4 cursor-pointer items-center justify-center  bg-[#003460] text-white transition-all duration-300 ease-in-out"
                onClick={toggleHouseholdMembers}
              >
                <span
                  className={`transition-transform duration-300 ease-in-out ${
                    isHouseholdMembersVisible ? "rotate-0" : "rotate-180"
                  }`}
                >
                  <img src={toggleDown} alt="down" className="h-8 w-8" />
                </span>
              </button>
            </div>
          </header>
          {isHouseholdMembersVisible && renderHouseholdMembersGrid}
          <AddNewButton
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsEditingHouseholdMember(false);
              setShowHouseholdMemberModal(true);
              householdMemberReset({
                first_name: "",
                last_name: "",
                email: "",
                date_of_birth: "",
                relationship: "",
                disability: false,
                military_vet: false,
                household_member_id: "",
                gender: "",
                ethnicity: "",
                id: "",
                created_at: "",
                updated_at: "",
                acc_created: "",
                is_deleted: false,
              });
            }}
            name="Add New Household Member"
          />
        </section>

        {/* Pets Section */}
        <section className="flex max-w-full flex-col items-start justify-start self-stretch bg-white pb-4 pt-4">
          <header
            className="mb-2 flex flex-row items-center justify-between self-stretch rounded bg-[#003460] p-2"
            id="householdinfo"
          >
            <div>
              <h2 className="text-xl font-bold text-white">Pets</h2>
            </div>
            <div className="flex items-center">
              <button
                type="button"
                className="flex h-4 w-4 cursor-pointer items-center justify-center  bg-[#003460] text-white transition-all duration-300 ease-in-out"
                onClick={togglePets}
              >
                <span
                  className={`transition-transform duration-300 ease-in-out ${
                    isPetsVisible ? "rotate-0" : "rotate-180"
                  }`}
                >
                  <img src={toggleDown} alt="down" className="h-8 w-8" />
                </span>
              </button>
            </div>
          </header>
          {isPetsVisible && renderPetsGrid}
          <AddNewButton
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsEditingPets(false);
              setShowPetsModal(true);
              petsReset({
                pet_name: "",
                pet_color: "",
                pet_coat: "",
                pet_breed: "",
                pet_size: "",
                is_deleted: false,
              });
            }}
            name="Add New Pet"
          />
        </section>
        <DialogComponent
          width="400px"
          isModal={true}
          visible={showPopup}
          close={handleClosePopup}
          header={null}
          content={dialogContent}
          showCloseIcon={true}
          closeOnEscape={true}
          target={document.body}
          cssClass="welcome-dialog"
        />
      </div>
      {showCompletionModal && (
        <Modal
          isOpen={showCompletionModal}
          onClose={() => setShowCompletionModal(false)}
          title="Citizen Profile Completed"
          width="800px"
          height="600px"
          noFooter={false}
          customLeftButtonText="Cancel"
          customRightButtonText="Grant Access"
          customOnLeftButtonClick={() => {
            setShowCompletionModal(false);
          }}
          customOnRightButtonClick={() => {
            setShowCompletionModal(false);
            setShowRecoveryTracker(true);
          }}
        >
          <div className="flex flex-col items-center justify-center text-center">
            <div className="mb-6">
              <div className="mx-auto h-16 w-16 rounded-full bg-green-500 p-4">
                <svg
                  viewBox="0 0 24 24"
                  className="h-8 w-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            </div>

            <div className="text-gray-600 mb-6 space-y-3 text-base">
              <p className="text-[24px] font-semibold">
                Sharing Your Information
              </p>
              <p>
                You can now choose to share your information with your local
                Long-Term Recovery Group (LTRG) and Certified Partners in the
                DARCI network
              </p>
              <p>
                Sharing information helps connect you to resources and support
                tailored to your situation
              </p>
              <p>
                Everyone in the DARCI network has completed a certification
                process to ensure your information is handled with the highest
                standards of privacy and security
              </p>
              <p>
                This is completely your choice, and you can change your sharing
                selections at any time.
              </p>
              <p>At DARCI, you control your information</p>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

CitizenProfile.propTypes = {
  className: PropTypes.string,
};

export default CitizenProfile;
